import React, { useState, useEffect, useCallback } from 'react';
import config, { authFetch } from '../config';
import PDFUploader from './PDFUploader';
import './PDFsList.css';

const PDFsList = ({ review, user, onSelectPDF, onBackToReviews, isSharedReview }) => {
  const [pdfs, setPdfs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showUploader, setShowUploader] = useState(false);
  const [activeGroup, setActiveGroup] = useState('all'); // 'all' | 'sharedWithMe'
  const [sharedWithMe, setSharedWithMe] = useState([]);

  const loadPDFs = useCallback(async () => {
    try {
      setLoading(true);
      const url = isSharedReview
        ? `${config.API_URL}/api/share/review/${review.shareId}`
        : `${config.API_URL}/api/reviews/${review.id}`;

      const [ownRes, sharedRes] = await Promise.all([
        authFetch(url),
        authFetch(`${config.API_URL}/api/shared/pdfs`),
      ]);

      if (!ownRes.ok) throw new Error('Failed to load PDFs');
      const ownData = await ownRes.json();
      setPdfs(ownData.pdfs || []);

      if (sharedRes.ok) {
        const sharedData = await sharedRes.json();
        setSharedWithMe(sharedData.pdfs || []);
      } else {
        setSharedWithMe([]);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [review.id, review.shareId, isSharedReview]);

  useEffect(() => {
    loadPDFs();
  }, [loadPDFs]);

  const copyReviewShareLink = (e) => {
    e.stopPropagation();
    if (!review.shareId) return;
    const url = `${window.location.origin}${window.location.pathname || ''}?shareReview=${review.shareId}`;
    navigator.clipboard.writeText(url).then(() => alert('Review link copied to clipboard!'));
  };

  const handlePDFUpload = async (url, pdfId, shareId) => {
    await loadPDFs();
    setShowUploader(false);
  };

  const handleDeletePDF = async (e, pdfId) => {
    e.stopPropagation();
    if (!window.confirm('Delete this PDF? This cannot be undone.')) return;

    try {
      const response = await authFetch(`${config.API_URL}/api/pdf/${pdfId}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to delete PDF');
      }
      setPdfs((prev) => prev.filter((p) => p.id !== pdfId));
    } catch (err) {
      alert(err.message || 'Failed to delete PDF');
    }
  };

  if (loading) {
    return (
      <div className="pdfs-container pdfs-container--loading">
        <div className="pdfs-loading-inline">
          <div className="loading-spinner" />
          <div className="loading-text">Loading PDFs…</div>
        </div>
      </div>
    );
  }

  const hasPdfs = pdfs.length > 0;
  const sharedWithMeOnly = sharedWithMe || [];

  let visiblePdfs = pdfs;
  if (activeGroup === 'sharedWithMe') {
    visiblePdfs = sharedWithMeOnly;
  }

  return (
    <div className="pdfs-container">
      <div className="pdfs-header">
        <div>
          <button onClick={onBackToReviews} className="back-button">
            ← Back to Reviews
          </button>
          <h1>{review.title}</h1>
          {review.description && <p className="review-description">{review.description}</p>}
          {review.shareId && (
            <div className="pdfs-header-actions">
              <button type="button" onClick={copyReviewShareLink} className="share-review-link-btn">
                Share review link
              </button>
            </div>
          )}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Group toggle: All vs Shared-with-me PDFs */}
      {(hasPdfs || sharedWithMeOnly.length > 0) && (
        <div className="pdfs-group-toggle">
          <button
            type="button"
            className={`group-toggle-btn ${activeGroup === 'all' ? 'group-toggle-btn--active' : ''}`}
            onClick={() => setActiveGroup('all')}
          >
            All PDFs
          </button>
          <button
            type="button"
            className={`group-toggle-btn ${activeGroup === 'sharedWithMe' ? 'group-toggle-btn--active' : ''}`}
            onClick={() => setActiveGroup('sharedWithMe')}
          >
            Shared with me
          </button>
        </div>
      )}

      {!isSharedReview && showUploader && (
        <div className="uploader-wrapper">
          <PDFUploader
            onPDFLoad={handlePDFUpload}
            reviewId={review.id}
            onCancel={() => setShowUploader(false)}
          />
        </div>
      )}

      <div className={`pdfs-list ${visiblePdfs.length === 0 && activeGroup !== 'sharedWithMe' && !isSharedReview && !showUploader ? 'pdfs-list--centered' : ''}`}>
        {activeGroup !== 'sharedWithMe' && !isSharedReview && !showUploader && (
          <button
            type="button"
            className="upload-card"
            onClick={() => setShowUploader(true)}
          >
            <div className="upload-card-icon">＋</div>
            <div className="upload-card-text">
              <h3>Add new PDF</h3>
              <p>Upload a file to start commenting and highlighting.</p>
            </div>
          </button>
        )}

        {visiblePdfs.length > 0 && visiblePdfs.map((pdf) => (
            <div
              key={pdf.id}
              className="pdf-card"
              onClick={() => onSelectPDF({ id: pdf.id, shareId: pdf.shareId })}
            >
              <button
                type="button"
                className="pdf-delete-btn"
                onClick={(e) => handleDeletePDF(e, pdf.id)}
                aria-label="Delete PDF"
                title="Delete PDF"
              >
                ✕
              </button>
              <div className="pdf-icon">📄</div>
              <div className="pdf-info">
                <h3>{pdf.filename}</h3>
                <p className="pdf-meta">
                  Uploaded {new Date(pdf.createdAt).toLocaleDateString()}
                </p>
              </div>
              <div className="pdf-arrow">→</div>
            </div>
          ))}
      </div>
    </div>
  );
};

export default PDFsList;
